/**
 * ProfileBanner — reusable entity summary card for profile pages.
 *
 * Sits between the page header and tab bar. Always visible regardless of
 * active tab. Designed to be shared across Server, Campus, and Resident profiles.
 *
 * Props:
 *   name        — Entity display name (e.g. server description)
 *   subtitle    — Secondary identifier (e.g. MAC address)
 *   statusDot   — "online" | "offline" | "unknown" | null (hides status)
 *   statusLabel — Text next to status dot (e.g. "Online", "Offline")
 *   statusTitle — Optional hover text explaining the status, when the label alone
 *                 does not carry it (e.g. a muted device that is still active)
 *   metaFields  — Array of { label, value } objects for the info grid
 *   chips       — Array of { key, label, icon?, tone? } feature indicator chips
 *                 rendered under the name/subtitle (e.g. Room Calling, Fall Protection)
 *   editRights  — Whether to show the Edit button
 *   onEdit      — Edit button callback
 *   loading     — Shows skeleton state
 */

const ProfileBanner = ({ name, subtitle, statusDot, statusLabel, statusTitle, metaFields = [], chips = [], editRights, onEdit, loading }) => {
  const statusClass = statusDot ? `pp-profile-banner__status--${statusDot}` : '';

  if (loading) {
    return (
      <div className="pp-profile-banner pp-profile-banner--loading">
        <div className="pp-profile-banner__identity">
          <div>
            <h1 className="pp-profile-banner__name">Loading...</h1>
            <p className="pp-profile-banner__subtitle">Loading...</p>
          </div>
        </div>
        <div className="pp-profile-banner__meta">
          {[1, 2, 3].map((i) => (
            <div key={i} className="pp-profile-banner__meta-item">
              <span className="pp-profile-banner__meta-label">—</span>
              <span className="pp-profile-banner__meta-value">Loading...</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="pp-profile-banner">
      {/* Left: Name + status */}
      <div className="pp-profile-banner__identity">
        <div>
          <h1 className="pp-profile-banner__name">{name || '—'}</h1>
          {subtitle && <p className="pp-profile-banner__subtitle">{subtitle}</p>}
          {chips.length > 0 && (
            <div className="pp-profile-banner__chips">
              {chips.map((chip, idx) => (
                <span
                  key={chip.key || idx}
                  className={`pp-profile-banner__chip${chip.tone ? ` pp-profile-banner__chip--${chip.tone}` : ''}`}
                  title={chip.title || undefined}
                >
                  {chip.icon || null}
                  {chip.label}
                </span>
              ))}
            </div>
          )}
        </div>
        {statusDot && (
          <span className={`pp-profile-banner__status ${statusClass}`} title={statusTitle}>
            <span className="pp-profile-banner__status-dot" />
            {statusLabel || statusDot}
          </span>
        )}
      </div>

      {/* Center: Meta fields grid */}
      {metaFields.length > 0 && (
        <div className="pp-profile-banner__meta">
          {metaFields.map((field, idx) => (
            <div key={idx} className="pp-profile-banner__meta-item">
              <span className="pp-profile-banner__meta-label">{field.label}</span>
              <span className="pp-profile-banner__meta-value">{field.value || '—'}</span>
            </div>
          ))}
        </div>
      )}

      {/* Right: Edit button */}
      {editRights && (
        <div className="pp-profile-banner__actions">
          <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={onEdit}>
            ✎ Edit
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileBanner;
